import React, { useState, useEffect } from 'react';
import styles from './token.module.scss';
import { API_BASE_URL } from '../../api';

const TokenManagementPanel = () => {
  const [tokenAccesses, setTokenAccesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingToken, setEditingToken] = useState(null);

  // Form state for creating new token access
  const [newTokenForm, setNewTokenForm] = useState({
    capital_expenditure_id: '',
    access_type: 'view',
    expires_at: '',
    notes: ''
  });

  // Form state for editing token access
  const [editForm, setEditForm] = useState({
    access_type: 'view',
    expires_at: '',
    is_active: true,
    notes: ''
  });

  useEffect(() => {
    fetchTokenAccesses();
  }, []);

  const fetchTokenAccesses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/token/management/`, {
        headers: {
          'Authorization': `Token ${localStorage.getItem('authToken')}`,
        },
      });

      const data = await response.json();

      if (data.status === 'success') {
        setTokenAccesses(data.data);
      } else {
        setError(data.message || 'Failed to fetch token accesses');
      }
    } catch (err) {
      setError('Error fetching token accesses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTokenAccess = async (e) => {
    e.preventDefault();
    
    // Log the data being sent for debugging
    console.log('Sending create data:', newTokenForm);
    
    try {
      const response = await fetch(`${API_BASE_URL}/token/access/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(newTokenForm),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setShowCreateForm(false);
        setNewTokenForm({
          capital_expenditure_id: '',
          access_type: 'view',
          expires_at: '',
          notes: ''
        });
        fetchTokenAccesses();
        window.alert('Token access created successfully!');
      } else {
        window.alert(data.message || 'Failed to create token access');
      }
    } catch (err) {
      window.alert('Error creating token access');
    }
  };

  const handleUpdateTokenAccess = async (e) => {
    e.preventDefault();
    
    // Log the data being sent for debugging
    console.log('Sending update data:', editForm);
    
    try {
      const response = await fetch(`${API_BASE_URL}/token/access/${editingToken.id}/update/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(editForm),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setEditingToken(null);
        setEditForm({
          access_type: 'view',
          expires_at: '',
          is_active: true,
          notes: ''
        });
        fetchTokenAccesses();
        window.alert('Token access updated successfully!');
      } else {
        window.alert(data.message || 'Failed to update token access');
      }
    } catch (err) {
      window.alert('Error updating token access');
    }
  };

  const handleDeleteTokenAccess = async (tokenId) => {
    if (!window.confirm('Are you sure you want to delete this token access?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/token/access/${tokenId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${localStorage.getItem('authToken')}`,
        },
      });

      const data = await response.json();

      if (data.status === 'success') {
        fetchTokenAccesses();
        window.alert('Token access deleted successfully!');
      } else {
        window.alert(data.message || 'Failed to delete token access');
      }
    } catch (err) {
        window.alert('Error deleting token access');
    }
  };

  const startEditing = (tokenAccess) => {
    setEditingToken(tokenAccess);
    setEditForm({
      access_type: tokenAccess.access_type,
      expires_at: tokenAccess.expires_at ? tokenAccess.expires_at.slice(0, 16) : '',
      is_active: tokenAccess.is_active,
      notes: tokenAccess.notes || ''
    });
  };

  const cancelEditing = () => {
    setEditingToken(null);
    setEditForm({
      access_type: 'view',
      expires_at: '',
      is_active: true,
      notes: ''
    });
  };

  if (loading) {
    return <div className={styles.loading}>Loading token accesses...</div>;
  }

  if (error) {
    return <div className={styles.errorMessage}>Error: {error}</div>;
  }

  return (
    <div className={styles.tokenManagementContainer}>
      <div className={styles.managementHeader}>
        <h2>🔑 टोकन पहुँच व्यवस्थापन</h2>
        <p>सबै टोकन पहुँचहरूको व्यवस्थापन गर्नुहोस्</p>
        <button
          onClick={() => setShowCreateForm(true)}
          className={styles.createButton}
        >
          ➕ नयाँ टोकन पहुँच सिर्जना गर्नुहोस्
        </button>
      </div>

      {/* Create New Token Access Form */}
      {showCreateForm && (
        <div className={styles.createForm}>
          <h3>नयाँ टोकन पहुँच सिर्जना गर्नुहोस्</h3>
          <form onSubmit={handleCreateTokenAccess}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Capital Expenditure ID:</label>
                <input
                  type="number"
                  value={newTokenForm.capital_expenditure_id}
                  onChange={(e) => setNewTokenForm({
                    ...newTokenForm,
                    capital_expenditure_id: e.target.value
                  })}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>पहुँच प्रकार:</label>
                <select
                  value={newTokenForm.access_type}
                  onChange={(e) => setNewTokenForm({
                    ...newTokenForm,
                    access_type: e.target.value
                  })}
                >
                  <option value="view">हेर्ने मात्र</option>
                  <option value="edit">सम्पादन गर्न सकिन्छ</option>
                  <option value="admin">प्रशासक पहुँच</option>
                </select>
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>म्याद (वैकल्पिक):</label>
                <input
                  type="datetime-local"
                  value={newTokenForm.expires_at}
                  onChange={(e) => setNewTokenForm({
                    ...newTokenForm,
                    expires_at: e.target.value
                  })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>टिप्पणी:</label>
                <textarea
                  value={newTokenForm.notes}
                  onChange={(e) => setNewTokenForm({
                    ...newTokenForm,
                    notes: e.target.value
                  })}
                  placeholder="टोकनको बारेमा टिप्पणी..."
                />
              </div>
            </div>
            <div className={styles.formActions}>
              <button type="submit" className={styles.submitButton}>
                सिर्जना गर्नुहोस्
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className={styles.cancelButton}
              >
                रद्द गर्नुहोस्
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Token Access List */}
      <div className={styles.tokenAccessList}>
        <h3>सबै टोकन पहुँचहरू</h3>
        <div className={styles.tableContainer}>
          <table className={styles.tokenTable}>
            <thead>
              <tr>
                <th>टोकन</th>
                <th>संस्था</th>
                <th>पहुँच प्रकार</th>
                <th>द्वारा दिइएको</th>
                <th>दिइएको मिति</th>
                <th>म्याद</th>
                <th>स्थिति</th>
                <th>कार्यहरू</th>
              </tr>
            </thead>
            <tbody>
              {tokenAccesses.map((tokenAccess) => (
                <tr key={tokenAccess.id} className={tokenAccess.is_expired ? styles.expired : ''}>
                  <td className={styles.tokenCell}>
                    <code>{tokenAccess.token}</code>
                  </td>
                  <td>{tokenAccess.capital_expenditure_details?.institution_name || 'N/A'}</td>
                  <td>
                    <span className={`${styles.accessBadge} ${styles[tokenAccess.access_type]}`}>
                      {tokenAccess.access_type_display}
                    </span>
                  </td>
                  <td>{tokenAccess.granted_by_name}</td>
                  <td>{new Date(tokenAccess.granted_at).toLocaleDateString('ne-NP')}</td>
                  <td>
                    {tokenAccess.expires_at ? 
                      new Date(tokenAccess.expires_at).toLocaleDateString('ne-NP') : 
                      'म्याद नभएको'
                    }
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${tokenAccess.is_active ? styles.active : styles.inactive}`}>
                      {tokenAccess.is_active ? 'सक्रिय' : 'निष्क्रिय'}
                    </span>
                  </td>
                  <td className={styles.actionCells}>
                    <button
                      onClick={() => startEditing(tokenAccess)}
                      className={styles.editButton}
                      title="सम्पादन गर्नुहोस्"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteTokenAccess(tokenAccess.id)}
                      className={styles.deleteButton}
                      title="मेटाउनुहोस्"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Token Access Modal */}
      {editingToken && (
        <div className={styles.editModal}>
          <div className={styles.modalContent}>
            <h3>टोकन पहुँच सम्पादन गर्नुहोस्</h3>
            <form onSubmit={handleUpdateTokenAccess}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>पहुँच प्रकार:</label>
                  <select
                    value={editForm.access_type}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      access_type: e.target.value
                    })}
                  >
                    <option value="view">हेर्ने मात्र</option>
                    <option value="edit">सम्पादन गर्न सकिन्छ</option>
                    <option value="admin">प्रशासक पहुँच</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>म्याद:</label>
                  <input
                    type="datetime-local"
                    value={editForm.expires_at}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      expires_at: e.target.value
                    })}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>स्थिति:</label>
                  <select
                    value={editForm.is_active}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      is_active: e.target.value === 'true'
                    })}
                  >
                    <option value={true}>सक्रिय</option>
                    <option value={false}>निष्क्रिय</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>टिप्पणी:</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      notes: e.target.value
                    })}
                    placeholder="टोकनको बारेमा टिप्पणी..."
                  />
                </div>
              </div>
              <div className={styles.formActions}>
                <button type="submit" className={styles.submitButton}>
                  अपडेट गर्नुहोस्
                </button>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className={styles.cancelButton}
                >
                  रद्द गर्नुहोस्
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenManagementPanel;
